import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, getDocs, query, where, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { useSchoolId } from '../lib/auth/schoolContext';
import { useUserRole, ROLES, hasRole } from '../lib/auth/roles';
import { getStaffDisplayName, getStaffRoleLabel } from '../lib/staff';
import StaffLeadSelect from './StaffLeadSelect';
import { suggestInterventionTargets } from '../lib/functions';
import { MAX_GROUP_SIZE } from '../lib/interventionLimits';
import InterventionSwapSuggestionsPanel from './InterventionSwapSuggestionsPanel';

const InterventionDashboard = () => {
  const [interventions, setInterventions] = useState([]);
  const { students, loading: studentsLoading } = useStudents();
  const [sessionLogs, setSessionLogs] = useState([]);
  const [selectedIntervention, setSelectedIntervention] = useState(null);
  const [details, setDetails] = useState({ staff: [], latestProgress: null, totalSessions: 0 });
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [assignStaffId, setAssignStaffId] = useState('');
  const [filters, setFilters] = useState({
    student: '',
    type: '',
    status: 'all',
    dateRange: 'all'
  });
  const [showRecentOnly, setShowRecentOnly] = useState(false);
  const [editingTargets, setEditingTargets] = useState(false);
  const [editingProgressNotes, setEditingProgressNotes] = useState(false);
  const [editingLeadStaff, setEditingLeadStaff] = useState(false);
  const [targetsEditData, setTargetsEditData] = useState([{ id: 1, text: '' }, { id: 2, text: '' }, { id: 3, text: '' }]);
  const [progressNotesEditData, setProgressNotesEditData] = useState('');
  const [savingTargets, setSavingTargets] = useState(false);
  const [savingProgressNotes, setSavingProgressNotes] = useState(false);
  const [suggestingTargets, setSuggestingTargets] = useState(false);
  const [targetRationales, setTargetRationales] = useState({});
  const listRef = useRef(null);
  const navigate = useNavigate();
  const { schoolId, loading: schoolIdLoading } = useSchoolId();
  const { role, loading: roleLoading } = useUserRole();

  useEffect(() => {
    if (!schoolIdLoading && schoolId) fetchData();
  }, [schoolId, schoolIdLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch interventions
      const interventionsQuery = query(
        collection(db, 'interventions'),
        where('schoolId', '==', schoolId)
      );
      const interventionsSnapshot = await getDocs(interventionsQuery);
      const interventionsData = interventionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by createdAt on the client side (newest first)
      interventionsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setInterventions(interventionsData);

      // Fetch session logs
      const logsQuery = query(
        collection(db, 'sessionLogs'),
        where('schoolId', '==', schoolId)
      );
      const logsSnapshot = await getDocs(logsQuery);
      const logsData = logsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      // Sort by createdAt on the client side (newest first)
      logsData.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
        const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
        return bTime - aTime;
      });
      setSessionLogs(logsData);

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch intervention data');
    } finally {
      setLoading(false);
    }

    // Staff list — separate try so intervention data still loads if staff query fails
    try {
      setStaffLoading(true);
      const staffQuery = query(collection(db, 'staff'), where('schoolId', '==', schoolId));
      const staffSnap = await getDocs(staffQuery);
      const staff = staffSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.active !== false);
      staff.sort((a, b) => getStaffDisplayName(a).localeCompare(getStaffDisplayName(b)));
      setStaffOptions(staff);
      if (staff.length === 0) {
        console.warn('InterventionDashboard: no active staff for school', schoolId);
      }
    } catch (staffErr) {
      console.error('Error fetching staff for lead assignment:', staffErr);
      setStaffOptions([]);
      toast.error('Could not load staff list. Check Staff Directory or sign out and back in');
    } finally {
      setStaffLoading(false);
    }
  };

  const openDetails = async (intervention) => {
    setSelectedIntervention(intervention);
    setAssignStaffId('');
    setEditingTargets(false);
    setEditingProgressNotes(false);
    setEditingLeadStaff(false);
    setDetailsLoading(true);
    
    // Initialize editing data
    const existingTargets = intervention.targets || [];
    setTargetsEditData([
      existingTargets[0] || { id: 1, text: '' },
      existingTargets[1] || { id: 2, text: '' },
      existingTargets[2] || { id: 3, text: '' }
    ]);
    setProgressNotesEditData(intervention.progressNotes || '');
    
    try {
      // Fetch staff assignments for this intervention.
      // Filtering by schoolId here is required so Firestore rules can
      // evaluate the `resource.data.schoolId == school()` check against a
      // bounded result set (otherwise stale docs without schoolId fail the rule).
      const assignmentsQuery = schoolId
        ? query(
            collection(db, 'interventionAssignments'),
            where('schoolId', '==', schoolId),
            where('interventionId', '==', intervention.id)
          )
        : query(
            collection(db, 'interventionAssignments'),
            where('interventionId', '==', intervention.id)
          );
      const assignmentsSnap = await getDocs(assignmentsQuery);
      const assignments = assignmentsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Unique staff IDs from assignments
      const staffIds = [...new Set(assignments.map(a => a.staffId).filter(Boolean))];
      let staff = [];
      if (staffIds.length) {
        // Scope staff query to this school so rules don't deny cross-tenant reads.
        const staffQ = schoolId
          ? query(collection(db, 'staff'), where('schoolId', '==', schoolId))
          : collection(db, 'staff');
        const staffSnap = await getDocs(staffQ);
        const staffAll = staffSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        staff = staffAll.filter(s => staffIds.includes(s.id));
      }

      // Session logs for this intervention (already have all logs; filter locally)
      const logs = sessionLogs.filter(l => l.interventionId === intervention.id);
      const totalSessions = logs.length;
      const sorted = [...logs].sort((a, b) => new Date(b.date) - new Date(a.date));
      const latestProgress = sorted[0]?.progress || null;

      // Incorporate direct lead staff field if present on intervention
      let directLead = null;
      if (intervention.leadStaffId) {
        const found = staffOptions.find(s => s.id === intervention.leadStaffId);
        directLead = found || (intervention.leadStaffName ? { id: intervention.leadStaffId, name: intervention.leadStaffName, role: intervention.leadStaffRole } : null);
      }
      setDetails({ staff: directLead ? [directLead] : staff, latestProgress, totalSessions });
    } catch (e) {
      console.error('Error loading intervention details', e);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setSelectedIntervention(null);
    setDetails({ staff: [], latestProgress: null, totalSessions: 0 });
    setEditingTargets(false);
    setEditingProgressNotes(false);
    setEditingLeadStaff(false);
  };

  // Check if current user can edit targets/progress notes
  const canEdit = () => {
    if (!selectedIntervention) return false;
    if (hasRole(role, ROLES.SENCO)) return true;
    const currentUserId = auth.currentUser?.uid;
    if (!currentUserId) return false;
    // Check if user is the lead staff (need to check staff collection)
    if (selectedIntervention.leadStaffId) {
      // We'd need to check if currentUserId matches leadStaffId via staff collection
      // For now, allow editing if user is lead staff (simplified check)
      return true; // This will be refined if we have user-staff mapping
    }
    return false;
  };

  const handleSuggestTargetsForExisting = async () => {
    if (!selectedIntervention?.id) return;
    setSuggestingTargets(true);
    try {
      const result = await suggestInterventionTargets({ interventionId: selectedIntervention.id });
      const suggested = (result?.data?.targets || []).slice(0, 3);
      if (suggested.length === 0) {
        toast.error('AI returned no targets. Try again.');
        return;
      }
      const padded = [0, 1, 2].map((i) => ({
        id: i + 1,
        text: suggested[i]?.text || '',
      }));
      const rationales = {};
      suggested.forEach((t, i) => {
        if (t.rationale) rationales[i + 1] = t.rationale;
      });
      setTargetsEditData(padded);
      setTargetRationales(rationales);
      toast.success(`AI generated ${suggested.length} SMART targets. Review and save.`);
    } catch (error) {
      console.error('Failed to suggest targets', error);
      toast.error(error?.message || 'Could not generate targets.');
    } finally {
      setSuggestingTargets(false);
    }
  };

  const saveTargets = async () => {
    if (!selectedIntervention) return;
    if (!canEdit()) {
      toast.error('You do not have permission to edit targets');
      return;
    }
    
    setSavingTargets(true);
    try {
      const cleanedTargets = targetsEditData
        .slice(0, 3)
        .map((t, idx) => ({ id: idx + 1, text: (t?.text || '').trim() }))
        .filter(t => t.text.length > 0);
      
      await updateDoc(doc(db, 'interventions', selectedIntervention.id), {
        targets: cleanedTargets,
        updatedAt: new Date(),
      });
      
      // Update local state
      setInterventions(prev => prev.map(i => 
        i.id === selectedIntervention.id ? { ...i, targets: cleanedTargets } : i
      ));
      setSelectedIntervention(prev => prev ? { ...prev, targets: cleanedTargets } : prev);
      setEditingTargets(false);
      toast.success('Targets updated successfully');
    } catch (e) {
      console.error('Failed to save targets', e);
      toast.error('Failed to save targets');
    } finally {
      setSavingTargets(false);
    }
  };

  const saveProgressNotes = async () => {
    if (!selectedIntervention) return;
    if (!canEdit()) {
      toast.error('You do not have permission to edit progress notes');
      return;
    }
    
    setSavingProgressNotes(true);
    try {
      await updateDoc(doc(db, 'interventions', selectedIntervention.id), {
        progressNotes: progressNotesEditData.trim(),
        progressNotesUpdatedAt: new Date(),
        updatedAt: new Date(),
      });
      
      // Update local state
      setInterventions(prev => prev.map(i => 
        i.id === selectedIntervention.id ? { ...i, progressNotes: progressNotesEditData.trim(), progressNotesUpdatedAt: new Date() } : i
      ));
      setSelectedIntervention(prev => prev ? { ...prev, progressNotes: progressNotesEditData.trim(), progressNotesUpdatedAt: new Date() } : prev);
      setEditingProgressNotes(false);
      toast.success('Progress notes updated successfully');
    } catch (e) {
      console.error('Failed to save progress notes', e);
      toast.error('Failed to save progress notes');
    } finally {
      setSavingProgressNotes(false);
    }
  };

  const assignLeadStaff = async () => {
    if (!selectedIntervention || !assignStaffId) return;
    try {
      const sel = staffOptions.find(s => s.id === assignStaffId);
      const leadName = getStaffDisplayName(sel);
      await updateDoc(doc(db, 'interventions', selectedIntervention.id), {
        leadStaffId: sel?.id || assignStaffId,
        leadStaffName: leadName,
        leadStaffRole: getStaffRoleLabel(sel?.role),
        leadStaffEmail: sel?.email || '',
        updatedAt: new Date(),
      });
      // update local state
      const leadRole = getStaffRoleLabel(sel?.role);
      setInterventions(prev => prev.map(i => i.id === selectedIntervention.id ? { ...i, leadStaffId: sel?.id || assignStaffId, leadStaffName: leadName, leadStaffRole: leadRole } : i));
      setSelectedIntervention(prev => prev ? { ...prev, leadStaffId: sel?.id || assignStaffId, leadStaffName: leadName, leadStaffRole: leadRole } : prev);
      setDetails(d => ({ ...d, staff: sel ? [sel] : [] }));
      // propagate to student assignments for this intervention so provision maps auto-populate
      try {
        const asnQ = schoolId
          ? query(
              collection(db, 'interventionAssignments'),
              where('schoolId', '==', schoolId),
              where('interventionId', '==', selectedIntervention.id)
            )
          : query(collection(db, 'interventionAssignments'), where('interventionId', '==', selectedIntervention.id));
        const asnSnap = await getDocs(asnQ);
        const updates = asnSnap.docs.map(a => updateDoc(doc(db, 'interventionAssignments', a.id), { staffId: sel?.id || assignStaffId, updatedAt: new Date() }));
        await Promise.all(updates);
      } catch (e) {
        console.warn('Failed to propagate lead staff to assignments', e);
      }
      toast.success('Lead staff assigned');
    } catch (e) {
      console.error('Failed to assign lead staff', e);
      toast.error('Failed to assign lead staff');
    }
  };

  const resolveStudentInfo = (item) => {
    // Accept either a string id or an object with id/name
    if (!item) return { id: '', name: 'Unknown Student' };
    const sid = typeof item === 'string' ? item : (item.id || item.studentId || '');
    const found = sid ? students.find(st => st.id === sid) : undefined;
    if (found) {
      const name = `${found.firstName || ''} ${found.lastName || ''}`.trim() || found.name || 'Unknown Student';
      return { id: found.id, name };
    }
    // Fallback to provided object name if present
    const fallbackName = typeof item === 'object' ? (item.name || item.fullName) : '';
    // Return empty id to avoid linking to a missing student route
    return { id: '', name: fallbackName || 'Unknown Student' };
  };

  const renderStudentLinks = (studentIds) => {
    if (!studentIds || studentIds.length === 0) return <span className="text-gray-500">No students assigned</span>;
    return (
      <>
        {studentIds.map((item, idx) => {
          const { id, name } = resolveStudentInfo(item);
          return (
            <span key={`${id}-${idx}`}>
              {id ? (
                <Link to={`/student/${id}`} className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>
                  {name}
                </Link>
              ) : (
                <span className="text-gray-700">{name}</span>
              )}
              {idx < studentIds.length - 1 ? ', ' : ''}
            </span>
          );
        })}
      </>
    );
  };

  const getInterventionStats = (interventionId) => {
    const logs = sessionLogs.filter(log => log.interventionId === interventionId);
    const totalSessions = logs.length;
    const recentSessions = logs.filter(log => {
      const logDate = new Date(log.date);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return logDate >= weekAgo;
    }).length;
    
    return { totalSessions, recentSessions };
  };

  const makeInactive = async (intervention) => {
    try {
      await updateDoc(doc(db, 'interventions', intervention.id), { status: 'inactive', updatedAt: new Date() });
      setInterventions(prev => prev.map(i => i.id === intervention.id ? { ...i, status: 'inactive' } : i));
      toast.success('Intervention marked inactive');
    } catch (e) {
      console.error('Failed to mark inactive', e);
      toast.error('Failed to update intervention');
    }
  };

  const deleteIntervention = async (intervention) => {
    try {
      if (!window.confirm('Delete this intervention? This cannot be undone.')) return;
      await deleteDoc(doc(db, 'interventions', intervention.id));
      setInterventions(prev => prev.filter(i => i.id !== intervention.id));
      toast.success('Intervention deleted');
    } catch (e) {
      console.error('Failed to delete intervention', e);
      toast.error('Failed to delete intervention');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      'Speech and Language Therapy': 'bg-purple-100 text-purple-800',
      'Occupational Therapy': 'bg-indigo-100 text-indigo-800',
      'Physical Therapy': 'bg-pink-100 text-pink-800',
      'Social Skills Group': 'bg-teal-100 text-teal-800',
      'Reading Intervention': 'bg-orange-100 text-orange-800',
      'Math Intervention': 'bg-red-100 text-red-800',
      'Behavioral Support': 'bg-amber-100 text-amber-800',
      'Sensory Integration': 'bg-cyan-100 text-cyan-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  // Some seed/legacy docs use `interventionType` instead of `type`. Normalise
  // here so filters and unique lists don't end up with `undefined` values that
  // would collapse to a single missing React key.
  const getInterventionType = (i) => i?.type || i?.interventionType || '';

  const filteredInterventions = interventions.filter(intervention => {
    if (filters.student && !(intervention.students || []).includes(filters.student)) return false;
    if (filters.type && getInterventionType(intervention) !== filters.type) return false;
    if (filters.status !== 'all' && intervention.status !== filters.status) return false;
    
    if (filters.dateRange !== 'all') {
      const startDate = new Date(intervention.startDate);
      const endDate = new Date(intervention.endDate);
      const today = new Date();
      
      switch (filters.dateRange) {
        case 'current':
          return startDate <= today && endDate >= today;
        case 'upcoming':
          return startDate > today;
        case 'past':
          return endDate < today;
        default:
          return true;
      }
    }
    
  if (showRecentOnly) {
    const stats = getInterventionStats(intervention.id);
    if (!stats.recentSessions) return false;
  }

  return true;
  });

  const uniqueTypes = [...new Set(interventions.map(getInterventionType).filter(Boolean))];
  const uniqueStatuses = [...new Set(interventions.map(i => i.status).filter(Boolean))];

  const activeStudentIds = useMemo(() => {
    const ids = new Set();
    interventions
      .filter(i => i.status === 'active')
      .forEach(i => (i.students || []).forEach(sid => ids.add(sid)));
    return ids;
  }, [interventions]);

  if (loading || studentsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <InterventionSwapSuggestionsPanel compact />
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button
          className="bg-white p-5 rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-blue-200 cursor-pointer transition-all text-left hover:scale-[1.02] group"
          onClick={() => {
            setFilters({ student: '', type: '', status: 'all', dateRange: 'all' });
            setShowRecentOnly(false);
            listRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Interventions</p>
              <p className="text-2xl font-bold text-gray-900">{interventions.length}</p>
            </div>
          </div>
        </button>

        <button
          className="bg-white p-5 rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-green-200 cursor-pointer transition-all text-left hover:scale-[1.02] group"
          onClick={() => {
            setFilters(prev => ({ ...prev, status: 'active' }));
            setShowRecentOnly(false);
            listRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {interventions.filter(i => i.status === 'active').length}
              </p>
            </div>
          </div>
        </button>

        <button
          className="bg-white p-5 rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-amber-200 cursor-pointer transition-all text-left hover:scale-[1.02] group"
          onClick={() => {
            setShowRecentOnly(true);
            listRef.current?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">This Week</p>
              <p className="text-2xl font-bold text-gray-900">
                {sessionLogs.filter(log => {
                  const logDate = new Date(log.date);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return logDate >= weekAgo;
                }).length}
              </p>
            </div>
          </div>
        </button>

        <button
          className="bg-white p-5 rounded-2xl shadow-md hover:shadow-xl border border-gray-100 hover:border-purple-200 cursor-pointer transition-all text-left hover:scale-[1.02] group"
          onClick={() => setShowStudentsModal(true)}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Students</p>
              <p className="text-2xl font-bold text-gray-900">{activeStudentIds.size}</p>
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 mb-8">
        <h3 className="text-base font-semibold text-gray-700 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Student</label>
            <select
              value={filters.student}
              onChange={(e) => setFilters(prev => ({ ...prev, student: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Students</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.firstName} {student.lastName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Dates</option>
              <option value="current">Current</option>
              <option value="upcoming">Upcoming</option>
              <option value="past">Past</option>
            </select>
          </div>
        </div>
      </div>

      {/* Interventions List */}
      <div ref={listRef} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <h3 className="text-base font-semibold text-gray-700">
            Interventions ({filteredInterventions.length})
          </h3>
        </div>
        
        {filteredInterventions.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2">No interventions found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {filteredInterventions.map((intervention) => {
              const stats = getInterventionStats(intervention.id);
              return (
                <div
                  key={intervention.id}
                  className="bg-white border border-gray-100 rounded-2xl shadow-md hover:shadow-xl transition-all cursor-pointer p-5 hover:scale-[1.02] hover:border-blue-200"
                  onClick={() => openDetails(intervention)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-gray-900 mb-1">{intervention.name}</h4>
                      {intervention.description && (
                        <p className="text-sm text-gray-600 line-clamp-2">{intervention.description}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {getInterventionType(intervention) && (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(getInterventionType(intervention))}`}>
                        {getInterventionType(intervention)}
                      </span>
                    )}
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(intervention.status)}`}>
                      {intervention.status}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="text-sm">
                      <span className="text-gray-600">Students: </span>
                      <span className="text-gray-900 font-medium">
                        {intervention.students?.length || 0}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Lead Staff: </span>
                      {intervention.leadStaffName ? (
                        <span className="text-gray-900">{intervention.leadStaffName}</span>
                      ) : (
                        <span className="text-blue-600 italic">Not assigned</span>
                      )}
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600">Sessions: </span>
                      <span className="text-gray-900 font-medium">
                        {stats.totalSessions} total
                        {stats.recentSessions > 0 && (
                          <span className="text-green-600 ml-1">({stats.recentSessions} this week)</span>
                        )}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {intervention.startDate} - {intervention.endDate}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetails(intervention);
                      }}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 shadow-md hover:shadow-lg transition-all text-sm font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Active Students Modal */}
      {showStudentsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Students in Active Interventions</h3>
                <p className="text-sm text-gray-500">Currently enrolled in at least one active intervention</p>
              </div>
              <button onClick={() => setShowStudentsModal(false)} className="text-gray-500 hover:text-gray-800">✕</button>
            </div>
            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {activeStudentIds.size === 0 ? (
                <div className="text-gray-500">No students in active interventions.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Interventions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {students
                        .filter(s => activeStudentIds.has(s.id))
                        .sort((a, b) => {
                          const order = {
                            'Reception': 0,
                            'Year 1': 1,
                            'Year 2': 2,
                            'Year 3': 3,
                            'Year 4': 4,
                            'Year 5': 5,
                            'Year 6': 6,
                            'Year 7': 7,
                            'Year 8': 8,
                            'Year 9': 9,
                            'Year 10': 10,
                            'Year 11': 11,
                          };
                          const ay = a.yearGroup in order ? order[a.yearGroup] : 999;
                          const by = b.yearGroup in order ? order[b.yearGroup] : 999;
                          if (ay !== by) return ay - by; // youngest to oldest
                          return (a.lastName || '').localeCompare(b.lastName || '');
                        })
                        .map((s) => {
                          const activeCount = interventions.filter(i => i.status === 'active' && (i.students || []).includes(s.id)).length;
                          return (
                            <tr key={s.id} className="hover:bg-gray-50">
                              <td className="px-6 py-3 text-sm text-gray-900">
                                <Link to={`/student/${s.id}`} className="text-blue-600 hover:underline">{s.firstName} {s.lastName}</Link>
                              </td>
                              <td className="px-6 py-3 text-sm text-gray-600">{s.yearGroup || '-'}</td>
                              <td className="px-6 py-3 text-sm text-gray-900">{activeCount}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button onClick={() => setShowStudentsModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Details Modal */}
      {selectedIntervention && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={closeDetails}>
          <div 
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-auto max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-2xl font-semibold text-gray-900">{selectedIntervention.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedIntervention.status)}`}>
                    {selectedIntervention.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{getInterventionType(selectedIntervention)}</p>
              </div>
              <button onClick={closeDetails} className="text-gray-500 hover:text-gray-800 text-2xl leading-none">✕</button>
            </div>

            {/* Scrollable Content */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {detailsLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Basic Info Section */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Description:</span>
                        <p className="text-gray-900 mt-1">{selectedIntervention.description || 'No description provided'}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Date Range:</span>
                        <p className="text-gray-900 mt-1">{selectedIntervention.startDate} - {selectedIntervention.endDate}</p>
                      </div>
                      {selectedIntervention.frequency && (
                        <div>
                          <span className="text-gray-600">Frequency:</span>
                          <p className="text-gray-900 mt-1 capitalize">{selectedIntervention.frequency}</p>
                        </div>
                      )}
                      {selectedIntervention.duration && (
                        <div>
                          <span className="text-gray-600">Duration:</span>
                          <p className="text-gray-900 mt-1">{selectedIntervention.duration}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Students Section */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Students Attending</h4>
                    {(selectedIntervention.students || []).length > MAX_GROUP_SIZE && (
                      <p className="mb-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        This group has {(selectedIntervention.students || []).length} students, which exceeds the maximum of {MAX_GROUP_SIZE}. Remove pupils or split into a second cohort.
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {(selectedIntervention.students || []).length === 0 ? (
                        <span className="text-gray-500">No students assigned</span>
                      ) : (
                        (selectedIntervention.students || []).map((item, idx) => {
                          const { id, name } = resolveStudentInfo(item);
                          return (
                            <span 
                              key={`${id}-${idx}`} 
                              className="px-3 py-1 bg-blue-50 text-blue-800 rounded-md text-sm"
                            >
                              {id ? (
                                <Link to={`/student/${id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
                                  {name}
                                </Link>
                              ) : (
                                name
                              )}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {/* Lead Staff Section */}
                  <div className="border-b pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Lead Staff</h4>
                    {selectedIntervention.leadStaffId && !editingLeadStaff ? (
                      <div className="text-sm">
                        <p className="text-gray-900 font-medium">
                          {selectedIntervention.leadStaffName || 'Assigned'}
                          {selectedIntervention.leadStaffRole && (
                            <span className="text-gray-600 ml-2">({selectedIntervention.leadStaffRole})</span>
                          )}
                        </p>
                        {hasRole(role, ROLES.SENCO) && (
                          <button
                            onClick={() => {
                              setEditingLeadStaff(true);
                              setAssignStaffId(selectedIntervention.leadStaffId);
                            }}
                            className="mt-2 text-blue-600 hover:underline text-sm"
                          >
                            Change lead staff
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <StaffLeadSelect
                          staff={staffOptions}
                          value={assignStaffId}
                          onChange={(e) => setAssignStaffId(e.target.value)}
                          loading={staffLoading || schoolIdLoading}
                          emptyLabel="No active staff. Add specialists in Staff Directory"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                        />
                        <button
                          onClick={async () => {
                            await assignLeadStaff();
                            setEditingLeadStaff(false);
                          }}
                          disabled={!assignStaffId}
                          className={`px-4 py-2 rounded text-white text-sm ${assignStaffId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`}
                        >
                          {selectedIntervention.leadStaffId ? 'Update' : 'Assign'}
                        </button>
                        {editingLeadStaff && (
                          <button
                            onClick={() => {
                              setEditingLeadStaff(false);
                              setAssignStaffId('');
                            }}
                            className="px-3 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Targets Section */}
                  <div className="border-b pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">Intervention Targets</h4>
                      {!editingTargets && canEdit() && (
                        <button
                          onClick={() => setEditingTargets(true)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingTargets ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={handleSuggestTargetsForExisting}
                            disabled={suggestingTargets}
                            className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${
                              suggestingTargets
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'bg-purple-600 text-white hover:bg-purple-700'
                            }`}
                            title="Generate SMART targets tailored to this cohort"
                          >
                            {suggestingTargets ? 'Generating…' : '✨ Suggest SMART targets with AI'}
                          </button>
                        </div>
                        {targetsEditData.slice(0, 3).map((target, idx) => (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-start gap-2">
                              <span className="mt-2 text-sm text-gray-500 w-6">{idx + 1}.</span>
                              <input
                                type="text"
                                value={targetsEditData[idx]?.text || ''}
                                onChange={(e) => {
                                  const updated = [...targetsEditData];
                                  updated[idx] = { id: idx + 1, text: e.target.value };
                                  setTargetsEditData(updated);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder={idx === 0 ? 'e.g., Read 10 CVC words with 90% accuracy by end of half-term' : 'Enter a SMART target'}
                              />
                            </div>
                            {targetRationales[idx + 1] && (
                              <p className="text-xs text-purple-700 italic pl-9">AI rationale: {targetRationales[idx + 1]}</p>
                            )}
                          </div>
                        ))}
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setEditingTargets(false);
                              setTargetRationales({});
                              const existingTargets = selectedIntervention.targets || [];
                              setTargetsEditData([
                                existingTargets[0] || { id: 1, text: '' },
                                existingTargets[1] || { id: 2, text: '' },
                                existingTargets[2] || { id: 3, text: '' }
                              ]);
                            }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveTargets}
                            disabled={savingTargets}
                            className={`px-4 py-2 rounded text-white text-sm ${savingTargets ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                          >
                            {savingTargets ? 'Saving...' : 'Save Targets'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(selectedIntervention.targets || []).length === 0 ? (
                          <p className="text-gray-500 text-sm">No targets set</p>
                        ) : (
                          (selectedIntervention.targets || []).map((target, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <span className="text-gray-500 text-sm w-6">{idx + 1}.</span>
                              <p className="text-gray-900 text-sm flex-1">{target.text}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  {/* Progress Notes Section */}
                  <div className="border-b pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">Progress Notes</h4>
                      {!editingProgressNotes && canEdit() && (
                        <button
                          onClick={() => setEditingProgressNotes(true)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                    {editingProgressNotes ? (
                      <div className="space-y-3">
                        <textarea
                          value={progressNotesEditData}
                          onChange={(e) => setProgressNotesEditData(e.target.value)}
                          rows={6}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          placeholder="Add ongoing progress notes for this intervention group..."
                        />
                        {selectedIntervention.progressNotesUpdatedAt && (
                          <p className="text-xs text-gray-500">
                            Last updated: {selectedIntervention.progressNotesUpdatedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                          </p>
                        )}
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setEditingProgressNotes(false);
                              setProgressNotesEditData(selectedIntervention.progressNotes || '');
                            }}
                            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={saveProgressNotes}
                            disabled={savingProgressNotes}
                            className={`px-4 py-2 rounded text-white text-sm ${savingProgressNotes ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                          >
                            {savingProgressNotes ? 'Saving...' : 'Save Notes'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        {selectedIntervention.progressNotes ? (
                          <div className="p-4 bg-blue-50 rounded-md">
                            <p className="text-gray-900 text-sm whitespace-pre-wrap">{selectedIntervention.progressNotes}</p>
                            {selectedIntervention.progressNotesUpdatedAt && (
                              <p className="text-xs text-gray-500 mt-2">
                                Last updated: {selectedIntervention.progressNotesUpdatedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No progress notes yet</p>
                        )}
                        {details.latestProgress && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-md">
                            <div className="text-xs font-medium text-gray-700 mb-1">Latest Session Progress</div>
                            <div className="text-sm text-gray-900 whitespace-pre-wrap">{details.latestProgress}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Session Statistics */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Session Statistics</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 rounded-md">
                        <div className="text-sm text-gray-600">Total Sessions</div>
                        <div className="text-2xl font-semibold text-gray-900">{details.totalSessions}</div>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-md">
                        <div className="text-sm text-gray-600">This Week</div>
                        <div className="text-2xl font-semibold text-gray-900">
                          {sessionLogs.filter(log => {
                            if (log.interventionId !== selectedIntervention.id) return false;
                            const logDate = new Date(log.date);
                            const weekAgo = new Date();
                            weekAgo.setDate(weekAgo.getDate() - 7);
                            return logDate >= weekAgo;
                          }).length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons Footer */}
            <div className="px-6 py-4 border-t flex items-center justify-between gap-3 flex-shrink-0 bg-gray-50">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    navigate(`/interventions?log=${selectedIntervention.id}`);
                    closeDetails();
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Log Session
                </button>
                {selectedIntervention.status !== 'inactive' && (
                  <button
                    onClick={() => {
                      makeInactive(selectedIntervention);
                      closeDetails();
                    }}
                    className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors text-sm font-medium"
                  >
                    Make Inactive
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    if (window.confirm('Delete this intervention? This cannot be undone.')) {
                      deleteIntervention(selectedIntervention);
                      closeDetails();
                    }
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
                <button
                  onClick={closeDetails}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionDashboard; 
